import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

import { Permissions } from '@/enums/permissions'
import { TaskList } from '@/enums/task'
import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

// The legacy portal's paths, kept as they were: bookmarks and the links in the API's e-mails
// (/confirmEmail/:code, /resetPass/:id) keep working. meta.permission is the portal
// permission the route needs.
const mainRoutes: RouteRecordRaw[] = [
    { path: '/', redirect: '/task/all' },

    // One component, the list chosen by the `taskList` prop.
    { path: '/task/all',            name: 'allTasks',            component: () => import('@/views/task/Tasks.vue'), props: { taskList: TaskList.Open },           meta: { requiresAuth: true } },
    { path: '/task/inConfirmation', name: 'tasksInConfirmation', component: () => import('@/views/task/Tasks.vue'), props: { taskList: TaskList.InConfirmation }, meta: { requiresAuth: true } },
    { path: '/task/finished',       name: 'finishedTasks',       component: () => import('@/views/task/Tasks.vue'), props: { taskList: TaskList.Finished },       meta: { requiresAuth: true } },

    { path: '/request', name: 'addRequest', component: () => import('@/views/request/AddRequest.vue'), meta: { requiresAuth: true, permission: Permissions.AddTask } },

    { path: '/projects',    name: 'projects', component: () => import('@/views/project/Projects.vue'), meta: { requiresAuth: true, permission: Permissions.ViewProjects } },
    { path: '/project/:id', name: 'project',  component: () => import('@/views/project/Project.vue'),  meta: { requiresAuth: true, permission: Permissions.ViewProjects } },

    { path: '/invoices',    name: 'invoices', component: () => import('@/views/invoice/Invoices.vue'), meta: { requiresAuth: true, permission: Permissions.ViewInvoices } },
    { path: '/invoice/:id', name: 'invoice',  component: () => import('@/views/invoice/Invoice.vue'),  meta: { requiresAuth: true, permission: Permissions.ViewInvoices } },
]

const publicRoutes: RouteRecordRaw[] = [
    { path: '/login',              name: 'login',            component: () => import('@/views/Login.vue'),                       meta: { layout: 'Blank', requiresAuth: false } },
    { path: '/register',           name: 'register',         component: () => import('@/views/public/Register.vue'),             meta: { layout: 'Blank', requiresAuth: false } },
    { path: '/confirmEmail/:code', name: 'confirm-mail',     component: () => import('@/views/public/ConfirmEmail.vue'),         meta: { layout: 'Blank', requiresAuth: false } },
    { path: '/resetPass',          name: 'reset-pass-start', component: () => import('@/views/public/RequestResetPassword.vue'), meta: { layout: 'Blank', requiresAuth: false } },
    { path: '/resetPass/:id',      name: 'reset-pass',       component: () => import('@/views/public/ResetPassword.vue'),        meta: { layout: 'Blank', requiresAuth: false } },

    { path: '/pages/error-404', name: 'page-error-404', component: () => import('@/views/public/Error404.vue'), meta: { layout: 'Blank', requiresAuth: false } },
    { path: '/:pathMatch(.*)*', redirect: '/pages/error-404' },
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior() {
        return { top: 0 }
    },
    routes: [...mainRoutes, ...publicRoutes],
})

router.beforeEach((to) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    // Protected unless the route opts out explicitly.
    if (to.meta.requiresAuth === false) {
        if (loggedIn && to.name === 'login') return { name: 'allTasks' }
        return true
    }

    if (!loggedIn) return { name: 'login' }

    // A route the user lacks the permission for is not entered (legacy: the navigation was
    // cancelled); a direct load lands on the start page instead of a blank screen.
    const permission = to.meta.permission as number | undefined
    if (permission && !appStore.hasPermission(permission)) {
        return router.currentRoute.value.matched.length ? false : { name: 'allTasks' }
    }

    return true
})

export default router
